import type { ClipboardEvent } from "react";

import { InternationalMdnInput } from "@expert/common-ui";
import { Button, Group, Input } from "@mantine/core";
import { useTimeout } from "@mantine/hooks";
import parsePhoneNumberFromString, { type CountryCode } from "libphonenumber-js";
import { useState } from "react";

import { useAnalytics, withPolymorphicClickAnalytics } from "@/analytics";
import { MdnInput } from "@/common-ui";
import { useAgentSdk } from "@/sdk";
import { useAsyncLoading } from "@/shared-utils";

import { isProd } from "../../constants";
import { randomZoomPhoneNumber } from "../../zoom";
import noCallControlsClasses from "./NoCallControls.module.css";

interface NoActiveCallProps {
    onAddCall: () => void;
    onCancel: () => void;
    defaultCountry?: CountryCode;
    isInternationalPhoneEnabled: boolean;
}

const NoCallControlsButton = withPolymorphicClickAnalytics(Button<"button">, "Click");

export function NewCallCallControl({
    onAddCall,
    onCancel,
    defaultCountry,
    isInternationalPhoneEnabled,
}: NoActiveCallProps) {
    const agentSdk = useAgentSdk();
    const { dispatcher } = useAnalytics();

    const [mdn, setMdn] = useState("");
    const [isMDNValid, setIsMDNValid] = useState(false);
    const [isEnteringNumber, setIsEnteringNumber] = useState(false);
    const [extension, setExtension] = useState<string>();
    const [pendingNewCall, setPendingNewCall] = useState(false);
    const { start, clear } = useTimeout(() => setPendingNewCall(false), 10000);

    const handleCallCustomer = async () => {
        setPendingNewCall(true);
        void dispatcher.dispatchBusinessEvent("NewCallOutboundInitiated", {
            outboundCallType: "TollFreeNumber",
        });
        await agentSdk.callCustomer(mdn, { extension });

        // Wait up to 10 seconds to prevent multiple clicks
        start();
    };

    const handleCallError = () => {
        // On error, immediately reset pendingNewCall state and clear timeout
        clear();
        setPendingNewCall(false);
    };

    const [newCallLoading, handleNewCall] = useAsyncLoading(handleCallCustomer, handleCallError);

    function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
        if (isProd) return;
        // for non-prod only, intercept Zoom link pasting:
        // 1. set MDN to a random Zoom phone number
        // 2. encode the meeting ID into an extension to dial with delays (`w`) and confirms (`#`)
        const plainTextData = e.clipboardData.getData("text/plain");
        const match = /^https:\/\/asurion\.zoom\.us\/j\/(?<meetingId>\d+)/.exec(plainTextData);
        if (match?.groups === undefined) return;
        e.preventDefault();
        const { meetingId } = match.groups;
        const zoomPhoneNumber = randomZoomPhoneNumber();
        e.currentTarget.value = zoomPhoneNumber;
        handleInput(zoomPhoneNumber);
        // each `w` is 500ms (see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#callsenddigitsdigits)
        setExtension(`${"w".repeat(8)}${Array.from(meetingId).join("w")}ww#${"w".repeat(8)}#`);
    }

    function handleInput(inputMDN: string) {
        const parsed = parsePhoneNumberFromString(inputMDN);
        setMdn(parsed?.number ?? inputMDN);
        setIsMDNValid(parsed?.isValid() ?? false);
        if (inputMDN) {
            setIsEnteringNumber(true);
        } else {
            setIsEnteringNumber(false);
        }
        setExtension(undefined);
    }

    function handleCancelAddCallClick() {
        setIsEnteringNumber(false);
        setIsMDNValid(false);
        setMdn("");
        setExtension(undefined);
        onCancel();
    }

    const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && !newCallLoading && isMDNValid) {
            await handleNewCall();
        }
    };

    const props = {
        value: mdn,
        defaultCountry: defaultCountry,
        onClick: onAddCall,
        onPaste: handlePaste,
        onInput: (e: React.InputEvent<HTMLInputElement>) => handleInput(e.currentTarget.value),
        autoFocus: true,
        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void handleKeyDown(event),
        variant: "unstyled",
        classNames: { input: noCallControlsClasses.input },
        placeholder: "Enter a number",
        "data-testid": "no-call-controls-input",
    };

    return (
        <Group gap="xs">
            <Input.Wrapper
                classNames={{ description: noCallControlsClasses.description }}
                description={isEnteringNumber ? "Enter a number" : null}
                flex={1}
            >
                {isInternationalPhoneEnabled ? <InternationalMdnInput {...props} /> : <MdnInput {...props} />}
            </Input.Wrapper>
            <Group gap="sm">
                <NoCallControlsButton
                    analytics={{
                        actionId: "NoCallControls_CancelNewCallButton",
                    }}
                    color="dark.1"
                    onClick={handleCancelAddCallClick}
                    disabled={newCallLoading || pendingNewCall}
                    size="xs"
                    className={noCallControlsClasses.cancelButton}
                    variant="transparent"
                    data-testid="no-call-controls-cancel-button"
                >
                    Cancel
                </NoCallControlsButton>
                <NoCallControlsButton
                    analytics={{
                        actionId: "NoCallControls_StartCallButton",
                    }}
                    color="primary.6"
                    disabled={!isMDNValid || newCallLoading || pendingNewCall}
                    loading={newCallLoading || pendingNewCall}
                    size="xs"
                    onClick={() => void handleNewCall()}
                    variant="filled"
                    data-testid="no-call-controls-call-button"
                >
                    Call
                </NoCallControlsButton>
            </Group>
        </Group>
    );
}
